import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  KeyRound,
  Save,
  Eye,
  EyeOff,
  Trash2,
  Loader2,
  RefreshCw,
  ExternalLink,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const groupByCategory = (keys) => {
  const map = new Map();
  for (const k of keys) {
    if (!map.has(k.category)) map.set(k.category, []);
    map.get(k.category).push(k);
  }
  return Array.from(map.entries()).map(([category, items]) => ({ category, items }));
};

function KeyRow({ entry, onSave, onClear }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState("");
  const [reveal, setReveal] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    const v = value.trim();
    if (!v) {
      toast.error("Enter a value (use Clear to remove an existing override)");
      return;
    }
    setSaving(true);
    try {
      await onSave(entry.key, v);
      setEditing(false);
      setValue("");
      setReveal(false);
    } finally {
      setSaving(false);
    }
  };

  const sourceBadge = entry.is_set ? (
    entry.source === "database" ? (
      <Badge variant="outline" className="text-[10px] border-emerald-500/30 text-emerald-300 bg-emerald-500/5">
        <CheckCircle2 className="w-3 h-3 mr-1" /> Live in DB
      </Badge>
    ) : (
      <Badge variant="outline" className="text-[10px] border-sky-400/30 text-sky-300 bg-sky-400/5">
        <CheckCircle2 className="w-3 h-3 mr-1" /> .env fallback
      </Badge>
    )
  ) : (
    <Badge variant="outline" className="text-[10px] border-rose-400/30 text-rose-300 bg-rose-400/5">
      <AlertCircle className="w-3 h-3 mr-1" /> Not set
    </Badge>
  );

  return (
    <Card
      className="p-5 bg-card border-border"
      data-testid={`api-keys-row-${entry.key.toLowerCase()}`}
    >
      <div className="flex items-start gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="text-sm font-semibold">{entry.label}</h4>
            <code className="text-[10px] text-muted-foreground bg-secondary/50 px-1.5 py-0.5 rounded">
              {entry.key}
            </code>
            {sourceBadge}
          </div>
          <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">
            {entry.description}
          </p>
          {entry.where_to_get && (
            <p className="text-[11px] text-muted-foreground/80 mt-1.5 flex items-center gap-1.5">
              <ExternalLink className="w-3 h-3" />
              {entry.where_to_get.startsWith("http") ? (
                <a
                  href={entry.where_to_get}
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary hover:underline"
                >
                  {entry.where_to_get}
                </a>
              ) : (
                <span>{entry.where_to_get}</span>
              )}
            </p>
          )}
          {entry.is_set && (
            <div className="mt-2 flex items-center gap-2 text-[11px] text-muted-foreground">
              <span>Current:</span>
              <code className="bg-secondary/40 px-2 py-0.5 rounded font-mono">{entry.masked || "(empty)"}</code>
              {entry.length > 0 && <span>· {entry.length} chars</span>}
              {entry.updated_at && (
                <span className="ml-1">
                  · updated {formatDate(entry.updated_at)}
                  {entry.updated_by ? ` by ${entry.updated_by}` : ""}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {!editing ? (
        <div className="flex items-center gap-2 mt-4">
          <Button
            size="sm"
            onClick={() => {
              setEditing(true);
              setValue("");
            }}
            data-testid={`api-keys-${entry.key.toLowerCase()}-edit`}
          >
            {entry.is_set ? (
              <>
                <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Update
              </>
            ) : (
              <>
                <Save className="w-3.5 h-3.5 mr-1.5" /> Set value
              </>
            )}
          </Button>
          {entry.is_set && entry.source === "database" && (
            <Button
              size="sm"
              variant="ghost"
              className="text-rose-400 hover:text-rose-300"
              onClick={() => onClear(entry)}
              data-testid={`api-keys-${entry.key.toLowerCase()}-clear`}
            >
              <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Remove override
            </Button>
          )}
        </div>
      ) : (
        <div className="mt-4 space-y-2">
          <div className="flex items-center gap-2">
            <Input
              type={reveal ? "text" : "password"}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={
                entry.is_secret === false ? "Paste the value" : "Paste the secret (hidden)"
              }
              autoFocus
              autoComplete="off"
              className="font-mono text-sm flex-1"
              data-testid={`api-keys-${entry.key.toLowerCase()}-input`}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => setReveal((r) => !r)}
              aria-label={reveal ? "Hide" : "Show"}
              className="h-9 w-9"
            >
              {reveal ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={handleSave}
              disabled={saving}
              data-testid={`api-keys-${entry.key.toLowerCase()}-save`}
            >
              {saving ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Saving
                </>
              ) : (
                <>
                  <Save className="w-3.5 h-3.5 mr-1.5" /> Save
                </>
              )}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setEditing(false);
                setValue("");
                setReveal(false);
              }}
              disabled={saving}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

const SCOPES = {
  admin: {
    title: "Frontpage API Keys",
    mono: "Frontpage Secrets",
    intro:
      "Credentials used by the public tylegn.com landing page. Right now this is just the contact form's email provider \u2014 social-platform keys live in Social Operator \u2192 API Keys.",
    categories: ["Email"],
  },
  social: {
    title: "Social Operator API Keys",
    mono: "Operator Secrets",
    intro:
      "Credentials used by the Social Operator: the AI assistant (Composer / replies / classification) and every social platform you publish to. Saving a value writes it to the database and it takes effect on the very next request \u2014 no server restart.",
    categories: [
      "AI",
      "Meta (Instagram + Facebook)",
      "TikTok",
      "YouTube",
      "X (Twitter)",
    ],
  },
};

export default function AdminApiKeys({ scope = "admin" }) {
  const { apiFetch } = useAuth();
  const cfg = SCOPES[scope] || SCOPES.admin;
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmClear, setConfirmClear] = useState(null);

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const data = await apiFetch("GET", "/api/admin/keys");
      const filtered = Array.isArray(data)
        ? data.filter((k) => cfg.categories.includes(k.category))
        : [];
      setKeys(filtered);
    } catch (err) {
      toast.error("Failed to load API keys");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope]);

  const handleSave = async (key, value) => {
    try {
      await apiFetch("PUT", `/api/admin/keys/${key}`, { value });
      toast.success(`${key} saved`);
      await load(true);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Save failed";
      toast.error(detail);
      throw err;
    }
  };

  const handleClearConfirmed = async () => {
    if (!confirmClear) return;
    try {
      await apiFetch("DELETE", `/api/admin/keys/${confirmClear.key}`);
      toast.success(`${confirmClear.key} override removed`);
      setConfirmClear(null);
      await load(true);
    } catch (err) {
      toast.error("Remove failed");
    }
  };

  const groups = useMemo(() => groupByCategory(keys), [keys]);

  return (
    <div className="max-w-4xl space-y-6" data-testid={`admin-api-keys-page-${scope}`}>
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <span className="mono-label text-primary text-[10px]">{cfg.mono}</span>
          <h2 className="text-2xl font-semibold mt-1 flex items-center gap-2" style={{ fontFamily: "var(--font-display)" }}>
            <KeyRound className="w-6 h-6 text-primary" />
            {cfg.title}
          </h2>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">{cfg.intro}</p>
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => load(true)}
          data-testid="admin-api-keys-refresh"
        >
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
        </Button>
      </header>

      <Card className="p-4 bg-secondary/40 border-border">
        <p className="text-xs text-muted-foreground leading-relaxed">
          <strong className="text-foreground">How it works:</strong> values you set here live
          in MongoDB and override the matching variable in <code className="text-foreground bg-card px-1 py-0.5 rounded text-[11px]">backend/.env</code>.
          <em> Live in DB</em> means the value below is the one currently being used.
          <em> .env fallback</em> means no override exists and the server is using the bootstrap value.
          <em> Not set</em> means neither — the related feature is in placeholder / disabled mode.
          Secret values are never returned by the API; you only ever see the masked preview.
        </p>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center min-h-[30vh]">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      ) : (
        groups.map((group, gi) => (
          <section key={group.category} className="space-y-3" data-testid={`api-keys-group-${gi}`}>
            <div className="flex items-center gap-3">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                {group.category}
              </h3>
              <Separator className="flex-1" />
              <Badge variant="outline" className="text-[10px]">
                {group.items.filter((i) => i.is_set).length} / {group.items.length} set
              </Badge>
            </div>
            <div className="grid grid-cols-1 gap-3">
              {group.items.map((entry) => (
                <KeyRow
                  key={entry.key}
                  entry={entry}
                  onSave={handleSave}
                  onClear={(e) => setConfirmClear(e)}
                />
              ))}
            </div>
          </section>
        ))
      )}

      <AlertDialog open={Boolean(confirmClear)} onOpenChange={(open) => !open && setConfirmClear(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this override?</AlertDialogTitle>
            <AlertDialogDescription>
              The database value for <strong>{confirmClear?.label}</strong> ({confirmClear?.key}) will be deleted.
              The server will fall back to the value in <code>backend/.env</code> (if any).
              This cannot be undone — re-paste the value if you change your mind.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleClearConfirmed}
              className="bg-rose-500 hover:bg-rose-600 text-white"
              data-testid="admin-api-keys-confirm-clear"
            >
              Remove override
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
